// Карточка подотчётного лица. Деталь, а не панель: у неё пять вкладок, а вкладки — признак
// детали (правило 1). Плюс стопка панелей остаётся свободной под мастера: положи карточку в
// панель — и мастеру выдачи поверх неё уже не хватит уровней.
//
// Пять вкладок из восьми разрешённых (правило 12).

import type { ReactNode } from 'react';
import { useState } from 'react';

import { RiWallet3Line } from '@remixicon/react';

import { DetailPane } from '@/layout/DetailPane';
import { ExportButton } from '@/shared/reports/ExportButton';
import { exportReconciliation } from '@/shared/api/endpoints/reports';
import type { PersonCardOut } from '@/shared/api/endpoints/accountable';
import { cn } from '@/utils/cn';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import { Block } from '@/shared/ui/block';
import { ДеньгиВПолях } from '@/shared/reports/ДеньгиВПолях';
import { Metric, MetricRow } from '@/shared/ui/metric';
import * as Button from '@/shared/ui/button';
import { CardTab, CardTabsList } from '@/shared/ui/card-tabs';
import * as TabMenuHorizontal from '@/shared/ui/tab-menu-horizontal';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { ListSkeleton } from '@/shared/ui/list-row';

import { BalanceHeader } from './BalanceHeader';
import { ДолгиСотрудника } from './ДолгиСотрудника';
import { ReversalDialog } from './ReversalDialog';
import { METHOD_TITLES, CLAIM_STATUS_TITLES, TRANSFER_STATUS_TITLES } from '../dictionaries';
import {
  useCashTrend,
  useClaims,
  useIssues,
  useReturns,
  useDecideClaim,
  useReverseIssue,
  useReverseReturn,
  useTransfers,
} from '../queries';
import type { PersonTab } from '../route';

export interface PersonCardProps {
  person: PersonCardOut;
  tab: PersonTab;
  onTab: (tab: PersonTab) => void;
  /** Открыть мастер. Без права кнопка не сереет, а объясняет (правило «гейт без объяснения»). */
  onIssue: () => void;
  onReturn: () => void;
  onTransfer: () => void;
  onNewClaim: () => void;
  /** Настройки подотчёта: порог подсветки и действующая подотчётность (тз, раздел 5.5). */
  onSettings: () => void;
  canIssue: boolean;
  /**
   * Право отменять денежную запись подотчёта.
   *
   * Отдельное от выдачи намеренно: выдают деньги старшие на объектах, а отменяют записи те, кто
   * ведёт учёт. Право есть в контракте ролей с первого дня, а спросить его было негде — маршруты
   * отмены не звал ни один экран.
   */
  canReverse: boolean;
  canReturn: boolean;
  canTransfer: boolean;
  canClaim: boolean;
}

const ВКЛАДКИ: ReadonlyArray<{ id: PersonTab; title: string }> = [
  { id: 'overview', title: 'Обзор' },
  { id: 'issues', title: 'Выдачи' },
  { id: 'claims', title: 'Заявки' },
  { id: 'transfers', title: 'Передачи' },
  { id: 'returns', title: 'Возвраты' },
];

export function PersonCard(props: PersonCardProps) {
  const { person, tab, onTab, onSettings } = props;
  // Отказ живёт НА ЭКРАНЕ, а не в системном окне браузера. Окно закрывается по «ОК» и не
  // оставляет ни следа: человек через секунду уже не помнит, что ему ответили, и жмёт снова.
  const [запрет, setЗапрет] = useState<string | null>(null);
  return (
    <DetailPane
      title={person.employee_name}
      actions={
        // Первичная кнопка ровно одна. Четыре одинаковых серых кнопки в ряд не дают ответа
        // на вопрос «а что тут обычно делают»: человек читает их слева направо каждый раз
        // заново. Выдача — то, ради чего эту карточку открывают чаще всего, поэтому она
        // залита цветом, а остальные три обведены.
        <div className='flex flex-wrap gap-2'>
          <Действие
            title='Оформить выдачу'
            основное
            allowed={props.canIssue}
            reason='Выдать деньги под отчёт может финансовый отдел. Попросите его оформить выдачу либо администратора выдать вам право.'
            onClick={props.onIssue}
            onRefuse={setЗапрет}
          />
          <Действие
            title='Оформить возврат'
            allowed={props.canReturn}
            reason='Возврат проводит финансовый отдел. Попросите его оформить либо администратора выдать вам право.'
            onClick={props.onReturn}
            onRefuse={setЗапрет}
          />
          <Действие
            title='Передать другому'
            allowed={props.canTransfer}
            reason='Передачу оформляет финансовый отдел или сам подотчётный. Попросите их либо администратора выдать вам право.'
            onClick={props.onTransfer}
            onRefuse={setЗапрет}
          />
          <Действие
            title='Отчитаться по чеку'
            allowed={props.canClaim}
            reason='Отчитывается подотчётное лицо либо финансовый отдел за него. Попросите их либо администратора выдать вам право.'
            onClick={props.onNewClaim}
            onRefuse={setЗапрет}
          />
        </div>
      }
      tabs={
        <TabMenuHorizontal.Root value={tab} onValueChange={(value) => onTab(value as PersonTab)}>
          <CardTabsList>
            {ВКЛАДКИ.map((вкладка) => (
              <CardTab key={вкладка.id} value={вкладка.id}>
                {вкладка.title}
              </CardTab>
            ))}
          </CardTabsList>
        </TabMenuHorizontal.Root>
      }
    >
      <BalanceHeader person={person} />
      {запрет !== null && (
        <Alert.Root variant='lighter' status='warning' size='small' className='mx-5 mt-3'>
          <Alert.Icon />
          {запрет}
        </Alert.Root>
      )}
      <div className='min-h-0 flex-1 overflow-y-auto'>
        {tab === 'overview' && <Обзор person={person} onSettings={onSettings} />}
        {tab === 'issues' && <Выдачи personId={person.id} можноОтменять={props.canReverse} />}
        {tab === 'claims' && <Заявки personId={person.id} можноОтменять={props.canReverse} />}
        {tab === 'transfers' && <Передачи personId={person.id} />}
        {tab === 'returns' && <Возвраты personId={person.id} можноОтменять={props.canReverse} />}
      </div>
    </DetailPane>
  );
}

function Действие({
  title,
  основное = false,
  allowed,
  reason,
  onClick,
  onRefuse,
}: {
  title: string;
  /** Главное действие карточки. Такое ровно одно: два «главных» — это ни одного. */
  основное?: boolean;
  allowed: boolean;
  reason: string;
  onClick: () => void;
  onRefuse: (причина: string) => void;
}) {
  // Кнопка остаётся нажимаемой и по нажатию объясняет: выключенная кнопка не доводит касание
  // до обработчика, и человек не получает НИЧЕГО — со стороны это сломанная программа.
  //
  // Объяснение показывается плашкой на самом экране. Раньше здесь стояло системное окно
  // браузера — единственное на весь веб: оно перекрывает страницу, уходит по «ОК» и не
  // оставляет после себя ни строчки, к которой можно вернуться. Правило 10 требует не только
  // причину, но и следующий шаг, а шаг надо ещё успеть прочитать.
  return (
    <Button.Root
      variant={основное ? 'primary' : 'neutral'}
      mode={основное ? 'filled' : 'stroke'}
      size='xsmall'
      onClick={() => (allowed ? onClick() : onRefuse(reason))}
    >
      {title}
    </Button.Root>
  );
}

function Обзор({ person, onSettings }: { person: PersonCardOut; onSettings: () => void }) {
  // Движение ЭТОГО человека за тридцать дней. Тот же вопрос, что на пульте, и тот же вид —
  // только про одного. Карточка отвечала «сколько на нём висит сейчас» и молчала про то,
  // копит он деньги или отчитывается: у человека, не отчитывающегося сорок четыре дня, и у
  // человека, который вчера получил и сегодня потратил, остаток может быть одинаковым, а
  // разговор с ними разный.
  const полоса = useCashTrend({ days: 30, accountable_person_id: person.id });
  // Две группы по смыслу, а не сетка из шести полей подряд. В сетке «Списано» стояло рядом с
  // «Порогом подсветки» — деньги рядом с настройкой, — и человек читал их как один список
  // равнозначных фактов. Сгруппированные, они отвечают на два разных вопроса: сколько денег
  // прошло через человека и как система его ведёт.
  return (
    <div className='flex flex-col gap-4 p-5'>
      <Block
        title='Что прошло через человека'
        // Акт сверки стоит ЗДЕСЬ, а не пятой кнопкой в шапке: в шапке четыре действия, и
        // пятая серая кнопка перестала бы отвечать на вопрос «а что тут делают обычно».
        // Здесь же он ровно на месте — рядом с числами, которые в нём и напечатаны.
        action={
          <ExportButton
            что='акт сверки'
            собрать={() => exportReconciliation({ accountable_person_id: person.id })}
          />
        }
      >
        <MetricRow columns={3}>
          {/* Подпись короткая, уточнение — в пояснении: «Оборот выдач за всё время» на
              ноутбучном экране обрезалось многоточием прямо посреди слова. */}
          <Metric
            label='Оборот выдач'
            value={formatKopecks(person.balance.issued)}
            note='за всё время'
          />
          <Metric label='Подтверждено чеками' value={formatKopecks(person.balance.confirmed)} />
          <Metric label='Списано' value={formatKopecks(person.balance.written_off)} />
        </MetricRow>
      </Block>

      {/* Долги живут в другом разделе, и это верно: подотчёт и долг — разные деньги. Но
          молчать о них здесь нельзя. Заказчик 21.09.2026 открыл карточку человека в «Деньгах»
          и не понял, долгов у него нет или их тут не показывают; на этой разнице решают,
          удерживать при расчёте или нет. */}
      <ДолгиСотрудника employeeId={person.employee_id} employeeName={person.employee_name} />

      {/* Полоса появляется, только когда за месяц было движение. Ровная линия по нулям
          отвечала бы «ничего не менялось» так же, как отсутствие данных, а это разные факты:
          первый значит «человек не работал с деньгами», второй — «мы не знаем». */}
      {полоса.data !== undefined &&
        (полоса.data.total_issued > 0 ||
          полоса.data.total_returned > 0 ||
          полоса.data.total_spent > 0) && (
          <Block title='Что менялось за тридцать дней'>
            <ДеньгиВПолях data={полоса.data} />
          </Block>
        )}

      {/* Действие на линии заголовка: блок целиком про две настройки — порог подсветки и
          действующую подотчётность, — и правятся они вместе. Раньше подсказка под порогом
          писала «задаётся в карточке», стоя в самой карточке, и не вела никуда: человек читал,
          где это делается, ровно там, где это делается, и не находил ни одного поля. */}
      <Block
        title='Как система его ведёт'
        action={
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={onSettings}
          >
            Настроить
          </Button.Root>
        }
      >
        <MetricRow columns={3}>
          <Metric
            label='Порог подсветки'
            value={person.soft_limit === null ? 'не задан' : formatKopecks(person.soft_limit)}
            note={
              person.soft_limit === null
                ? // «Не задан» и «подсвечивать нечем» — это одно и то же, сказанное дважды.
                  // Пояснение обязано добавлять то, чего в значении нет: что делать.
                  'пока не задан, строка в пульте не краснеет ни при какой сумме'
                : 'выше него строка краснеет в пульте'
            }
          />
          <Metric
            label='Давность отчёта'
            value={
              person.oldest_unreported_days === null
                ? 'нечему висеть'
                : person.oldest_unreported_days === 0
                  ? 'меньше суток'
                  : `${person.oldest_unreported_days} дн.`
            }
          />
          <Metric
            label='Состояние'
            value={person.is_active ? 'действующее' : 'отключено'}
            tone={person.is_active ? 'обычный' : 'приглушённый'}
          />
        </MetricRow>
      </Block>
    </div>
  );
}

/**
 * Шапка денежного списка: что перечислено, сколько и переключатель «с учётом сторно».
 *
 * Правило 8: сторнированное остаётся в списке ПЕРЕЧЁРКНУТЫМ, у каждого денежного списка есть
 * переключатель. Без него сторнированная выдача просто исчезала с вкладки — остаток при этом
 * менялся, и найти пропавшую запись человеку было негде. Правило 2 требует ещё и подписи
 * «что перечислено и сколько»: столбик сумм без числа сверху не список, а полотно.
 */
function ШапкаСписка({
  подпись,
  сСторно,
  переключить,
}: {
  подпись: string;
  /** Не задан — список и так отдаётся целиком, переключать нечего (передачи, возвраты). */
  сСторно?: boolean;
  переключить?: () => void;
}) {
  return (
    <div className='flex items-center justify-between gap-3 px-5 pt-3'>
      <h4 className='text-label-sm text-text-strong-950'>{подпись}</h4>
      {переключить !== undefined && (
        <button
          type='button'
          onClick={переключить}
          className='shrink-0 text-paragraph-xs text-text-sub-600 underline underline-offset-2'
        >
          {сСторно === true ? 'Только действующие' : 'С учётом сторно'}
        </button>
      )}
    </div>
  );
}

/**
 * Строка денежного списка. Сторнированная остаётся видимой и перечёркнутой.
 *
 * `отмена` — готовое окно подтверждения либо пусто, если отменять нечем: нет права, запись уже
 * отменена или это сама обратная запись. Решает это вызывающий, а не строка: у выдачи, возврата
 * и заявки разные поля состояния, и строка, которая полезла бы в них сама, знала бы про три
 * разных предмета сразу.
 */
function ДенежнаяСтрока({
  слева,
  сумма,
  сторнирована,
  отмена,
}: {
  слева: string;
  сумма: number;
  сторнирована: boolean;
  отмена?: ReactNode;
}) {
  return (
    <li className='flex items-baseline justify-between gap-3 px-5 py-3'>
      <span
        className={cn(
          'min-w-0 truncate text-paragraph-sm text-text-sub-600',
          сторнирована && 'text-text-sub-600 line-through',
        )}
      >
        {слева}
      </span>
      <span
        className={cn(
          'shrink-0 text-label-sm tabular-nums text-text-strong-950',
          сторнирована && 'text-text-sub-600 line-through',
        )}
      >
        {formatKopecks(сумма)}
      </span>
      {отмена !== undefined && <span className='shrink-0'>{отмена}</span>}
    </li>
  );
}

function Выдачи({ personId, можноОтменять }: { personId: string; можноОтменять: boolean }) {
  const [сСторно, setССторно] = useState(false);
  const запрос = useIssues({ person_id: personId, with_reversed: сСторно });
  const отменить = useReverseIssue();
  return (
    <ScreenState
      data={запрос.data}
      error={запрос.error as never}
      subject='выдачи под отчёт'
      isEmpty={(данные) => данные.items.length === 0 && !сСторно}
      loading={<ListSkeleton />}
      empty={
        <EmptyState
          icon={RiWallet3Line}
          title='Выдач не было'
          illustration='деньги'
          description='Деньги под отчёт этому человеку ещё не выдавали.'
        />
      }
      onRetry={() => void запрос.refetch()}
    >
      {(данные) => (
        <>
          <ШапкаСписка
            подпись={countLabel(данные.items.length, ['выдача', 'выдачи', 'выдач'])}
            сСторно={сСторно}
            переключить={() => setССторно((прежнее) => !прежнее)}
          />
          <ul className='divide-y divide-stroke-soft-200'>
            {данные.items.map((строка) => (
              <ДенежнаяСтрока
                key={строка.id}
                слева={
                  `${formatDateOnly(строка.issued_on ?? строка.issued_at)} · ${METHOD_TITLES[строка.method]}` +
                  (строка.purpose === null ? '' : ` · ${строка.purpose}`) +
                  (строка.reversal_basis === null ? '' : ` · сторно: ${строка.reversal_basis}`)
                }
                сумма={строка.amount}
                сторнирована={строка.status === 'reversed' || строка.reversal_of_id !== null}
                отмена={
                  можноОтменять &&
                  строка.status !== 'reversed' &&
                  строка.reversal_of_id === null ? (
                    <ReversalDialog
                      предмет='выдачу'
                      сумма={строка.amount}
                      идёт={отменить.isPending}
                      ошибка={отменить.isError ? отменить.error : null}
                      отменить={(основание) =>
                        отменить.mutateAsync({ issueId: строка.id, basis: основание })
                      }
                    />
                  ) : undefined
                }
              />
            ))}
          </ul>
        </>
      )}
    </ScreenState>
  );
}

function Заявки({ personId, можноОтменять }: { personId: string; можноОтменять: boolean }) {
  const [сСторно, setССторно] = useState(false);
  const отменить = useDecideClaim();
  const запрос = useClaims({ person_id: personId, with_reversed: сСторно });
  return (
    <ScreenState
      data={запрос.data}
      error={запрос.error as never}
      subject='заявки подотчётного лица'
      isEmpty={(данные) => данные.items.length === 0 && !сСторно}
      loading={<ListSkeleton />}
      empty={
        <EmptyState
          icon={RiWallet3Line}
          title='Заявок нет'
          illustration='заявки'
          description='Человек ещё не отчитывался за полученные деньги.'
        />
      }
      onRetry={() => void запрос.refetch()}
    >
      {(данные) => (
        <>
          <ШапкаСписка
            подпись={countLabel(данные.items.length, ['заявка', 'заявки', 'заявок'])}
            сСторно={сСторно}
            переключить={() => setССторно((прежнее) => !прежнее)}
          />
          <ul className='divide-y divide-stroke-soft-200'>
            {данные.items.map((строка) => (
              <ДенежнаяСтрока
                key={строка.id}
                слева={`${строка.category_title} · ${строка.title} · ${CLAIM_STATUS_TITLES[строка.status]}`}
                сумма={строка.amount}
                сторнирована={строка.status === 'reversed' || строка.reversal_of_id !== null}
                отмена={
                  // Отменяется только ПРИНЯТАЯ заявка: поданную решают в очереди приёмки, а
                  // отклонённая денег и не создавала.
                  можноОтменять &&
                  строка.status === 'accepted' &&
                  строка.reversal_of_id === null ? (
                    <ReversalDialog
                      предмет='заявку'
                      сумма={строка.amount}
                      идёт={отменить.isPending}
                      ошибка={отменить.isError ? отменить.error : null}
                      отменить={(основание) =>
                        отменить.mutateAsync({
                          claimId: строка.id,
                          decision: 'reverse',
                          reason: основание,
                        })
                      }
                    />
                  ) : undefined
                }
              />
            ))}
          </ul>
        </>
      )}
    </ScreenState>
  );
}

function Передачи({ personId }: { personId: string }) {
  const запрос = useTransfers({ person_id: personId });
  return (
    <ScreenState
      data={запрос.data}
      error={запрос.error as never}
      subject='передачи остатка'
      isEmpty={(данные) => данные.items.length === 0}
      loading={<ListSkeleton />}
      empty={
        <EmptyState
          icon={RiWallet3Line}
          title='Передач не было'
          illustration='обмен'
          description='Остаток никому не передавали и от других не принимали.'
        />
      }
      onRetry={() => void запрос.refetch()}
    >
      {(данные) => (
        <>
          <ШапкаСписка
            подпись={countLabel(данные.items.length, ['передача', 'передачи', 'передач'])}
          />
          <ul className='divide-y divide-stroke-soft-200'>
            {данные.items.map((строка) => (
              <ДенежнаяСтрока
                key={строка.id}
                слева={`${строка.from_employee_name} → ${строка.to_employee_name} · ${TRANSFER_STATUS_TITLES[строка.status]}`}
                сумма={строка.amount}
                // Сервер отдаёт передачи все, включая отклонённые и сторнированные, — и это
                // верно: денежная запись не исчезает. Но выглядели они как действующие, и
                // столбик сумм не сходился с остатком ни у кого.
                сторнирована={строка.status === 'reversed' || строка.status === 'declined'}
              />
            ))}
          </ul>
        </>
      )}
    </ScreenState>
  );
}

function Возвраты({ personId, можноОтменять }: { personId: string; можноОтменять: boolean }) {
  // Сторнированные показываются ВСЕГДА, без переключателя: возвратов у человека единицы, а
  // отменённый возврат — единственное объяснение, почему остаток не сошёлся.
  const запрос = useReturns({ person_id: personId, with_reversed: true });
  const отменитьВозврат = useReverseReturn();
  return (
    <ScreenState
      data={запрос.data}
      error={запрос.error as never}
      subject='возвраты подотчётных средств'
      isEmpty={(данные) => данные.items.length === 0}
      loading={<ListSkeleton />}
      empty={
        <EmptyState
          icon={RiWallet3Line}
          title='Возвратов не было'
          illustration='обмен'
          description='Остаток в кассу не возвращали.'
        />
      }
      onRetry={() => void запрос.refetch()}
    >
      {(данные) => (
        <>
          <ШапкаСписка
            подпись={countLabel(данные.items.length, ['возврат', 'возврата', 'возвратов'])}
          />
          <ul className='divide-y divide-stroke-soft-200'>
            {данные.items.map((строка) => (
              <ДенежнаяСтрока
                key={строка.id}
                слева={
                  `${formatDateOnly(строка.returned_at)} · ${METHOD_TITLES[строка.method]}` +
                  (строка.reversal_basis === null ? '' : ` · сторно: ${строка.reversal_basis}`)
                }
                сумма={строка.amount}
                // До 07.09.2026 здесь стояло жёсткое «не сторнирована»: сервер отдавал
                // состояние с самого начала, а карточка рисовала отменённый возврат обычной
                // строкой. Отменённые двадцать тысяч продолжали выглядеть возвращёнными.
                сторнирована={строка.status === 'reversed' || строка.reversal_of_id !== null}
                отмена={
                  можноОтменять &&
                  строка.status !== 'reversed' &&
                  строка.reversal_of_id === null ? (
                    <ReversalDialog
                      предмет='возврат'
                      сумма={строка.amount}
                      идёт={отменитьВозврат.isPending}
                      ошибка={отменитьВозврат.isError ? отменитьВозврат.error : null}
                      отменить={(основание) =>
                        отменитьВозврат.mutateAsync({ returnId: строка.id, basis: основание })
                      }
                    />
                  ) : undefined
                }
              />
            ))}
          </ul>
        </>
      )}
    </ScreenState>
  );
}
